/**
 * Types for n8n REST API v1.
 * These types represent the contract between Webflow and n8n services.
 *
 * Based on n8n's public REST API documentation.
 */

// =============================================================================
// Workflow Types
// =============================================================================

export interface N8nNode {
  id: string;
  name: string;
  type: string;
  typeVersion?: number;
  position: [number, number];
  parameters?: Record<string, unknown>;
  credentials?: Record<string, {id: string; name: string}>;
  webhookId?: string;
  disabled?: boolean;
}

export interface N8nWorkflowSettings {
  executionOrder?: 'v1';
  saveExecutionProgress?: boolean;
  saveManualExecutions?: boolean;
  saveDataErrorExecution?: 'all' | 'none';
  saveDataSuccessExecution?: 'all' | 'none';
  executionTimeout?: number;
  timezone?: string;
}

export interface N8nWorkflow {
  id: string;
  name: string;
  active: boolean;
  nodes: N8nNode[];
  connections: Record<string, unknown>;
  settings?: N8nWorkflowSettings;
  tags?: Array<{id: string; name: string}>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWorkflowInput {
  name: string;
  nodes: N8nNode[];
  connections: Record<string, unknown>;
  settings: N8nWorkflowSettings;
  projectId?: string;
}

export interface UpdateWorkflowInput {
  name?: string;
  nodes?: N8nNode[];
  connections?: Record<string, unknown>;
  settings?: N8nWorkflowSettings;
}

// =============================================================================
// Execution Types
// =============================================================================

export const EXECUTION_STATUSES = [
  'canceled',
  'crashed',
  'error',
  'queued',
  'running',
  'success',
  'waiting',
] as const;

export type ExecutionStatus = (typeof EXECUTION_STATUSES)[number];

export const TERMINAL_EXECUTION_STATUSES = [
  'canceled',
  'crashed',
  'error',
  'success',
] as const;

export type TerminalExecutionStatus =
  (typeof TERMINAL_EXECUTION_STATUSES)[number];

export type ExecutionMode =
  | 'cli'
  | 'error'
  | 'integrated'
  | 'internal'
  | 'manual'
  | 'retry'
  | 'trigger'
  | 'webhook';

export interface N8nNodeExecutionData {
  startTime: number;
  executionTime: number;
  executionStatus: 'success' | 'error';
  // `previousNodeRun` (the upstream run that fed this one) lets us trace a
  // loop-body run back to the loop pass that produced it.
  source: Array<{previousNode: string; previousNodeRun?: number}>;
  data?: {
    main: Array<Array<{json: Record<string, unknown>}>>;
  };
  error?: {message: string; description?: string};
}

export interface N8nExecutionResultData {
  runData: Record<string, N8nNodeExecutionData[]>;
  lastNodeExecuted?: string;
}

export interface N8nExecution {
  id: string;
  workflowId: string;
  status: ExecutionStatus;
  mode: ExecutionMode;
  startedAt: string;
  createdAt?: string;
  deletedAt?: string | null;
  stoppedAt: string | null;
  finished: boolean;
  data?: {
    resultData?: N8nExecutionResultData;
    [key: string]: unknown;
  };
  retryOf?: string | null;
  retrySuccessId?: string | null;
  waitTill?: string | null;
}

export interface ExecutionListOptions {
  status?: ExecutionStatus;
  workflowId?: string;
  limit?: number;
  cursor?: string;
  includeData?: boolean;
}

// =============================================================================
// Credential Types
// =============================================================================

export interface N8nCredential {
  id: string;
  name: string;
  type: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCredentialInput {
  name: string;
  type: string;
  data: Record<string, unknown>;
}

export interface UpdateCredentialInput {
  name?: string;
  data?: Record<string, unknown>;
}

// =============================================================================
// Variable Types
// =============================================================================

export interface N8nVariable {
  id: string;
  key: string;
  value: string;
  type: string;
}

// =============================================================================
// Webhook Types
// =============================================================================

export interface WebhookTriggerInput {
  workflowId: string;
  webhookPath: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  query?: Record<string, string>;
}

export interface WebhookTriggerResult {
  executionId: string;
  data?: unknown;
}

// =============================================================================
// Project Types
// =============================================================================

export interface N8nProject {
  id: string;
  name: string;
  type?: string;
}

export interface CreateProjectInput {
  name: string;
}

export interface TransferWorkflowInput {
  destinationProjectId: string;
}

// =============================================================================
// Paginated Response
// =============================================================================

export interface PaginatedResponse<T> {
  data: T[];
  nextCursor: string | null;
}
